import React from "react";
import { Draggable } from "react-beautiful-dnd";
import { FaGripLines, FaCircleXmark } from "react-icons/fa6";
import { isValidUrl } from "./constants.js";

/**
 * A single menu card within a region's horizontal strip: a 960x640 (3:2)
 * thumbnail plus an optional link field. Owns its Draggable wrapper.
 *
 * @param {{image: string, link: string, _localId: string}} props.card
 * @param {number} props.index
 * @param {(patch: {link: string}) => void} props.onChange
 * @param {() => void} props.onRemove
 * @param {boolean} props.allowDeepLinks - permit saucey:// links (mobile cards)
 */
const MenuCardTile = ({ card, index, onChange, onRemove, allowDeepLinks = false }) => {
  const linkInvalid = !isValidUrl(card.link, allowDeepLinks);

  return (
    <Draggable draggableId={card._localId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={
            "shrink-0 w-60 rounded-lg border bg-white dark:bg-theme-surface-accent " +
            (snapshot.isDragging
              ? "border-theme-button-primary shadow-lg"
              : "border-slate-200 dark:border-slate-700")
          }
        >
          {/* Thumbnail */}
          <div className="relative aspect-[3/2] rounded-t-lg overflow-hidden bg-slate-100 dark:bg-slate-800">
            <img
              src={card.image}
              alt="menu card"
              className="w-full h-full object-cover"
              draggable={false}
            />
            {/* Drag handle */}
            <span
              {...provided.dragHandleProps}
              className="absolute top-1.5 left-1.5 p-1.5 rounded bg-black/40 text-white cursor-grab active:cursor-grabbing"
              title="Drag to reorder"
            >
              <FaGripLines className="w-3.5 h-3.5" />
            </span>
            {/* Remove */}
            <button
              type="button"
              onClick={onRemove}
              className="absolute top-1.5 right-1.5 text-white/90 hover:text-white drop-shadow"
              title="Remove card"
            >
              <FaCircleXmark className="w-5 h-5" />
            </button>
          </div>

          {/* Optional link */}
          <div className="p-2">
            <input
              type="text"
              value={card.link}
              onChange={(e) => onChange({ link: e.target.value })}
              placeholder={
                allowDeepLinks
                  ? "Optional link (https:// or saucey://)"
                  : "Optional link (https://…)"
              }
              className={
                "form-input w-full text-xs " +
                (linkInvalid ? "border-rose-500 focus:border-rose-500" : "")
              }
            />
            {linkInvalid && (
              <p className="mt-1 text-xs text-rose-500">
                {allowDeepLinks
                  ? "Must be a valid http(s) or saucey:// link"
                  : "Must be a valid http(s) URL"}
              </p>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default MenuCardTile;
